import {
  APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS,
  parseApplicationOntologyPublicationRefs,
  type ApplicationOntologyPublicationRefs,
} from "@cogo/acceptance-contracts";

export const PUBLICATION_VERIFICATION_AXES = [
  "schema",
  "typecheck",
  "build",
  "interaction",
  "business-error",
  "server-contract",
  "roundtrip",
  "auth",
  "timeout",
  "retry",
  "visual",
  "security",
  "deployment-readback",
  "live-server-readback",
] as const;

export type PublicationVerificationAxis =
  (typeof PUBLICATION_VERIFICATION_AXES)[number];

export const PUBLICATION_CANDIDATE_REQUIRED_VERIFICATION_AXES = [
  "schema",
  "typecheck",
  "build",
  "interaction",
  "business-error",
  "server-contract",
  "roundtrip",
  "auth",
  "timeout",
  "retry",
  "visual",
  "security",
] as const satisfies readonly PublicationVerificationAxis[];

export type PublicationVerificationStatus = "pass" | "fail" | "not-proven";
export type PublicationGateStatus = "ready" | "blocked";

/**
 * The state universe as a RUNTIME array, with the type derived from it —
 * not the reverse. A union type is invisible at runtime, so a gate over
 * "every state has a localized label" had nothing to iterate and the raw
 * enum reached users instead (slice 6 F3). This array is that gate's
 * producer: add a state here and both the copy tables (a `Record` keyed by
 * this type) and `publication-release-card-honesty.test.tsx` demand a label
 * for it.
 */
export const PUBLICATION_RELEASE_STATES = [
  "draft",
  "compiling",
  "candidate_blocked",
  "candidate_ready",
  "verification_running",
  "verification_blocked",
  "certification_candidate",
  "authorization_pending",
  "authorized",
  "deploying",
  "deployment_readback_pending",
  "live_readback_pending",
  "production_ready",
  "failed",
  "rolled_back",
  "taken_down",
] as const;

export type PublicationReleaseState = (typeof PUBLICATION_RELEASE_STATES)[number];

export type PublicationReleaseAction =
  | "prepare-candidate"
  | "run-verification"
  | "request-staging-grant"
  | "request-authorization"
  | "deploy-staging"
  | "deploy-production"
  | "refresh-readback"
  | "rollback"
  | "take-down";

export interface PublicationContentRef {
  ref: string;
  contentHash: string;
}

export interface PublicationProofArtifactRef extends PublicationContentRef {
  axis: PublicationVerificationAxis;
}

export type PublicationCompilerIdentityProjection =
  | {
      availability: "available";
      compilerId: string;
      compilerVersion: string;
      implementationRef: string;
      compilerArtifactRef: PublicationContentRef;
      deterministic: true;
      modelFinalWriterAllowed: false;
      artifactAttestation: {
        schemaVersion: "cogo.publication-compiler-artifact-attestation.v1";
        scope: "candidate";
        signatureStatus: "unsigned";
        reason: "compiler-artifact-signature-not-provided";
      };
    }
  | {
      availability: "unavailable";
      reason: "historical-release-compiler-authority-unavailable";
    };

export interface PublicationVerificationAxisProjection {
  axis: PublicationVerificationAxis;
  status: PublicationVerificationStatus;
  evidenceRefs: string[];
  notes: string[];
}

export interface PublicationVerificationGateProjection {
  status: PublicationGateStatus;
  missingAxes: PublicationVerificationAxis[];
  failedAxes: PublicationVerificationAxis[];
}

export interface PublicationVerificationMatrixProjection {
  schemaVersion: "cogo.publication-verification-matrix.v1";
  targetId: string;
  targetProfileHash: string;
  planHash: string;
  outputManifestHash: string;
  axes: PublicationVerificationAxisProjection[];
  candidateGate: PublicationVerificationGateProjection;
  productionGate: PublicationVerificationGateProjection;
  productionPublishAllowed: false;
  contentHash: string;
}

export interface PublicationTargetProfileProjection {
  profileId: string;
  profileVersion: string;
  contentHash: string;
  title?: string;
  lifecycle: "experimental" | "candidate" | "active" | "deprecated";
  supportStatus: "candidate" | "partial" | "supported" | "unsupported";
  openGaps: string[];
  deploymentProfile?: string;
}

export function parsePublicationTargetProfileProjection(
  input: unknown,
): PublicationTargetProfileProjection {
  const target = record(input);
  return {
    profileId: requiredText(target.profileId, "targetProfile.profileId"),
    profileVersion: requiredText(
      target.profileVersion,
      "targetProfile.profileVersion",
    ),
    contentHash: requiredHash(target.contentHash, "targetProfile.contentHash"),
    ...(text(target.title) ? { title: text(target.title) } : {}),
    lifecycle: normalizeOneOf(
      target.lifecycle,
      ["experimental", "candidate", "active", "deprecated"],
      "candidate",
    ),
    supportStatus: normalizeOneOf(
      target.supportStatus,
      ["candidate", "partial", "supported", "unsupported"],
      "candidate",
    ),
    openGaps: strings(target.openGaps),
    ...(text(target.deploymentProfile)
      ? { deploymentProfile: text(target.deploymentProfile) }
      : {}),
  };
}

export interface PublicationAuthorizationProjection {
  status: "pending" | "verified" | "rejected" | "expired" | "consumed";
  authorizationRef?: PublicationContentRef;
  environment?: "production";
  expiresAt?: string;
}

export interface PublicationExecutionGrantProjection {
  status: "pending" | "verified" | "rejected" | "expired" | "consumed";
  grantRef?: PublicationContentRef;
  environment?: "test" | "preview" | "staging";
  expiresAt?: string;
}

export interface PublicationDeploymentProjection {
  status:
    | "pending"
    | "deploying"
    | "reported"
    | "readback-pending"
    | "verified"
    | "failed"
    | "rolled-back"
    | "taken-down";
  environment: "test" | "preview" | "staging" | "production";
  operation?: "deploy" | "rollback" | "take-down";
  outputHash?: string;
  provider?: string;
  deploymentId?: string;
  url?: string;
  receiptRef?: PublicationContentRef;
  failureReasons: string[];
}

export interface PublicationReadbackProjection {
  status: "pending" | "pass" | "fail" | "rolled-back" | "taken-down";
  readbackRef?: PublicationContentRef;
  deploymentArtifactMatch?: boolean;
  liveServerMatch?: boolean;
  checkedAt?: string;
  failureReasons: string[];
}

export interface PublicationRollbackTargetProjection {
  releaseId: string;
  receiptRef: PublicationContentRef;
  outputHash: string;
  environment: "test" | "preview" | "staging" | "production";
  readbackRef: PublicationContentRef;
}

export interface PublicationReleaseProjection {
  schemaVersion: "cogo.studio-publication-release.v1";
  releaseId: string;
  candidateId: string;
  outputHash: string;
  projectId: string;
  createdAt: string;
  updatedAt: string;
  sourceRevision: PublicationContentRef & { stale: boolean };
  applicationPackageRef?: PublicationContentRef;
  /**
   * Present exactly when the SERVER projected the closed sixteen. Absent for
   * historical releases that were never bound to a generated application —
   * absence is a real state here, not a missing value.
   */
  applicationOntology?: ApplicationOntologyPublicationRefs;
  compilerIdentity: PublicationCompilerIdentityProjection;
  targetProfile: PublicationTargetProfileProjection;
  publicationRequestRef?: PublicationContentRef;
  planRef?: PublicationContentRef;
  outputManifestRef?: PublicationContentRef;
  promotionRef?: PublicationContentRef;
  proofArtifactRefs: PublicationProofArtifactRef[];
  verificationMatrix?: PublicationVerificationMatrixProjection;
  postDeployVerificationMatrix?: PublicationVerificationMatrixProjection;
  attestationRef?: PublicationContentRef;
  executionGrant?: PublicationExecutionGrantProjection;
  authorization?: PublicationAuthorizationProjection;
  deployment?: PublicationDeploymentProjection;
  readback?: PublicationReadbackProjection;
  rollbackTargets: PublicationRollbackTargetProjection[];
  serverDerivedState: PublicationReleaseState;
  derivedState: PublicationReleaseState;
  blockers: string[];
  allowedActions: PublicationReleaseAction[];
}

/**
 * ── the generated-application publication binding ────────────────────────
 *
 * `ApplicationOntologyPublicationRefs` is IMPORTED, never redeclared: sixteen
 * opaque content refs and nothing else. Studio holds no ontology authority, so
 * everything it can honestly do with them is (a) refuse a server projection
 * that is not exactly those sixteen distinct roles, and (b) carry them back
 * unaltered on every later operation about the same release.
 *
 * WHY DISTINCTNESS IS CHECKED HERE. The imported parser is shape-closed —
 * exactly sixteen keys, each an exact `ContentRef`. It does not, and should
 * not, know that these sixteen name sixteen DIFFERENT things. A projection
 * that gives the admitted ontology INPUT and the generated executable OUTPUT
 * one address satisfies every shape rule and is precisely the confusion this
 * program exists to catch; the same is true of the topology slice collapsed
 * onto the capability slice. Collapse is checked on BOTH `ref` and
 * `contentHash`, because the same bytes under two names is the same collapse
 * wearing a second label.
 */
export const PUBLICATION_ONTOLOGY_REFS_ROLE_COLLISION =
  "APPLICATION_ONTOLOGY_PUBLICATION_REFS_ROLE_COLLISION";

/**
 * The refusal for lineage that came from the browser rather than the server.
 * An offered value is REFUSED, not ignored — a silently dropped forged field
 * is how a forged field becomes a field nobody reads.
 */
export const PUBLICATION_ONTOLOGY_REFS_CLIENT_AUTHORED =
  "PUBLICATION_ONTOLOGY_REFS_CLIENT_AUTHORED";

/**
 * The ONE closed parse every publication surface uses — the projection reader
 * and every request send site alike. Delegates shape to the canonical imported
 * parser and adds only the role-distinctness refusal described above.
 */
export function parsePublicationApplicationOntologyRefs(
  value: unknown,
): ApplicationOntologyPublicationRefs {
  const refs = parseApplicationOntologyPublicationRefs(value);
  const byRef = new Map<string, string>();
  const byHash = new Map<string, string>();
  for (const key of APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS) {
    const reference = refs[key];
    const collidedWith =
      byRef.get(reference.ref) ?? byHash.get(reference.contentHash);
    if (collidedWith) {
      throw new Error(
        `${PUBLICATION_ONTOLOGY_REFS_ROLE_COLLISION}:${collidedWith}:${key}`,
      );
    }
    byRef.set(reference.ref, key);
    byHash.set(reference.contentHash, key);
  }
  return refs;
}

/**
 * Refuses a caller/agent-supplied ref set on any Studio publication input.
 * Presence is the whole test: there is no legitimate browser source for these.
 */
export function assertNoClientAuthoredPublicationOntologyRefs(
  input: unknown,
): void {
  if (
    input !== null &&
    typeof input === "object" &&
    Object.hasOwn(input, "applicationOntology")
  ) {
    throw new Error(PUBLICATION_ONTOLOGY_REFS_CLIENT_AUTHORED);
  }
}

export interface PublicationCandidateRequest {
  targetProfileId: string;
  targetProfileVersion?: string;
  sourceRevisionHash?: string;
  /**
   * REQUIRED key, nullable value. Optional would let a new call site drop the
   * binding by saying nothing; a required key makes every send site state
   * explicitly whether this release has ontology identity. `undefined` is the
   * honest answer for a historical unbound release.
   */
  applicationOntology: ApplicationOntologyPublicationRefs | undefined;
}

export interface PublicationDeployRequest {
  environment: "preview" | "staging" | "production";
  adapterId: string;
  confirmation: {
    confirmed: true;
    candidateId: string;
    outputHash: string;
    promotionHash?: string;
  };
}

export interface PublicationDeployTransportRequest extends PublicationDeployRequest {
  idempotencyKey: string;
  executionCredential: string;
  applicationOntology: ApplicationOntologyPublicationRefs | undefined;
}

export interface PublicationCredentialResult {
  release: PublicationReleaseProjection;
  executionCredential: string;
}

export interface PublicationRollbackTransportRequest {
  targetDeploymentReceiptRef: PublicationContentRef;
  idempotencyKey: string;
  executionCredential: string;
  applicationOntology: ApplicationOntologyPublicationRefs | undefined;
  confirmation: {
    confirmed: true;
    candidateId: string;
    outputHash: string;
    targetDeploymentReceiptRef: PublicationContentRef;
  };
}

export type PublicationTakeDownTransportRequest =
  PublicationRollbackTransportRequest;

const RELEASE_STATES = new Set<PublicationReleaseState>([
  "draft",
  "compiling",
  "candidate_blocked",
  "candidate_ready",
  "verification_running",
  "verification_blocked",
  "certification_candidate",
  "authorization_pending",
  "authorized",
  "deploying",
  "deployment_readback_pending",
  "live_readback_pending",
  "production_ready",
  "failed",
  "rolled_back",
  "taken_down",
]);

const RELEASE_ACTIONS = new Set<PublicationReleaseAction>([
  "prepare-candidate",
  "run-verification",
  "request-staging-grant",
  "request-authorization",
  "deploy-staging",
  "deploy-production",
  "refresh-readback",
  "rollback",
  "take-down",
]);

const AXES = new Set<string>(PUBLICATION_VERIFICATION_AXES);

export function parsePublicationReleaseProjection(
  input: unknown,
): PublicationReleaseProjection {
  const root = record(input);
  const raw = record(
    root.release ?? root.publicationRelease ?? root.projection ?? root,
  );
  const schemaVersion = text(raw.schemaVersion);
  if (schemaVersion !== "cogo.studio-publication-release.v1") {
    throw new Error("Unsupported publication release response");
  }

  const releaseId = requiredText(raw.releaseId, "releaseId");
  const projectId = requiredText(raw.projectId, "projectId");
  const source = record(raw.sourceRevision);
  const target = record(raw.targetProfile);
  const serverDerivedState = normalizeReleaseState(
    raw.derivedState ?? raw.state,
  );
  const matrix = parseVerificationMatrix(raw.verificationMatrix);
  const authorization = parseAuthorization(raw.authorization);
  const executionGrant = parseExecutionGrant(raw.executionGrant);
  const deployment = parseDeployment(raw.deployment);
  const readback = parseReadback(raw.readback);
  const targetProfile = parsePublicationTargetProfileProjection(target);
  const compilerIdentity = parseCompilerIdentity(raw.compilerIdentity);

  const blockers = uniqueStrings(strings(raw.blockers));
  const allowedActions = strings(raw.allowedActions).filter(
    (action): action is PublicationReleaseAction =>
      RELEASE_ACTIONS.has(action as PublicationReleaseAction),
  );
  const projection: PublicationReleaseProjection = {
    schemaVersion: "cogo.studio-publication-release.v1",
    releaseId,
    candidateId: requiredText(raw.candidateId, "candidateId"),
    outputHash: requiredHash(raw.outputHash, "outputHash"),
    projectId,
    createdAt: requiredText(raw.createdAt, "createdAt"),
    updatedAt: requiredText(raw.updatedAt, "updatedAt"),
    sourceRevision: {
      ref: requiredText(source.ref, "sourceRevision.ref"),
      contentHash: requiredHash(
        source.contentHash,
        "sourceRevision.contentHash",
      ),
      stale: source.stale === true,
    },
    applicationPackageRef: parseContentRef(raw.applicationPackageRef),
    // NOT `parseContentRef`'s falsy-tolerant shape: only a projection that
    // carries no such key at all is unbound. `null`, `{}`, or fifteen refs are
    // a bound release whose identity did not survive, and are refused.
    ...(raw.applicationOntology === undefined
      ? {}
      : {
          applicationOntology: parsePublicationApplicationOntologyRefs(
            raw.applicationOntology,
          ),
        }),
    compilerIdentity,
    targetProfile,
    publicationRequestRef: parseContentRef(raw.publicationRequestRef),
    planRef: parseContentRef(raw.planRef),
    outputManifestRef: parseContentRef(raw.outputManifestRef),
    promotionRef: parseContentRef(raw.promotionRef),
    proofArtifactRefs: parseProofArtifactRefs(raw.proofArtifactRefs),
    verificationMatrix: matrix,
    postDeployVerificationMatrix: parseVerificationMatrix(
      raw.postDeployVerificationMatrix,
    ),
    attestationRef: parseContentRef(raw.attestationRef),
    executionGrant,
    authorization,
    deployment,
    readback,
    rollbackTargets: parseRollbackTargets(raw.rollbackTargets),
    serverDerivedState,
    derivedState: serverDerivedState,
    blockers,
    allowedActions,
  };

  projection.derivedState = deriveFailClosedReleaseState(projection);
  projection.allowedActions = filterAllowedActions(projection);
  projection.blockers = uniqueStrings([
    ...projection.blockers,
    ...deriveClientBlockers(projection),
  ]);
  return projection;
}

function parseCompilerIdentity(
  input: unknown,
): PublicationCompilerIdentityProjection {
  if (input === undefined || input === null) {
    return {
      availability: "unavailable",
      reason: "historical-release-compiler-authority-unavailable",
    };
  }
  const compiler = record(input);
  if (compiler.availability === "unavailable") {
    if (
      compiler.reason !== "historical-release-compiler-authority-unavailable"
    ) {
      throw new Error("Invalid compiler identity unavailable reason");
    }
    return {
      availability: "unavailable",
      reason: "historical-release-compiler-authority-unavailable",
    };
  }
  if (compiler.availability !== "available") {
    throw new Error("Invalid compiler identity availability");
  }
  if (
    compiler.deterministic !== true ||
    compiler.modelFinalWriterAllowed !== false
  ) {
    throw new Error("Invalid compiler identity policy");
  }
  const attestation = record(compiler.artifactAttestation);
  if (
    attestation.schemaVersion !==
      "cogo.publication-compiler-artifact-attestation.v1" ||
    attestation.scope !== "candidate" ||
    attestation.signatureStatus !== "unsigned" ||
    attestation.reason !== "compiler-artifact-signature-not-provided"
  ) {
    throw new Error("Invalid compiler artifact attestation");
  }
  const compilerArtifactRef = parseContentRef(compiler.compilerArtifactRef);
  if (!compilerArtifactRef) {
    throw new Error("Missing compiler artifact reference");
  }
  return {
    availability: "available",
    compilerId: requiredText(
      compiler.compilerId,
      "compilerIdentity.compilerId",
    ),
    compilerVersion: requiredText(
      compiler.compilerVersion,
      "compilerIdentity.compilerVersion",
    ),
    implementationRef: requiredText(
      compiler.implementationRef,
      "compilerIdentity.implementationRef",
    ),
    compilerArtifactRef,
    deterministic: true,
    modelFinalWriterAllowed: false,
    artifactAttestation: {
      schemaVersion: "cogo.publication-compiler-artifact-attestation.v1",
      scope: "candidate",
      signatureStatus: "unsigned",
      reason: "compiler-artifact-signature-not-provided",
    },
  };
}

export function deriveFailClosedReleaseState(
  release:
    | Omit<PublicationReleaseProjection, "derivedState">
    | PublicationReleaseProjection,
): PublicationReleaseState {
  if (
    release.serverDerivedState === "taken_down" ||
    release.deployment?.status === "taken-down"
  ) {
    const takeDownVerified =
      release.deployment?.status === "taken-down" &&
      release.deployment.operation === "take-down" &&
      Boolean(release.deployment.receiptRef) &&
      release.readback?.status === "taken-down" &&
      Boolean(release.readback.readbackRef) &&
      release.readback.deploymentArtifactMatch === true &&
      release.readback.liveServerMatch === true;
    return takeDownVerified ? "taken_down" : "live_readback_pending";
  }
  if (
    release.serverDerivedState === "rolled_back" ||
    release.readback?.status === "rolled-back"
  )
    return "rolled_back";
  if (
    release.serverDerivedState === "failed" ||
    release.readback?.status === "fail" ||
    release.deployment?.status === "failed"
  )
    return "failed";
  if (release.sourceRevision.stale) return "verification_blocked";

  if (selectedMatrixBindingBlockers(release).length > 0) {
    return "verification_blocked";
  }

  const matrix = effectiveProductionMatrix(release);
  if (release.serverDerivedState === "production_ready") {
    const profileReady =
      release.targetProfile.lifecycle === "active" &&
      release.targetProfile.supportStatus === "supported" &&
      release.targetProfile.openGaps.length === 0;
    const evidenceReady = matrix?.productionGate.status === "ready";
    const authorizationReady =
      release.authorization?.status === "consumed" ||
      release.authorization?.status === "verified";
    const deploymentReady =
      release.deployment?.status === "verified" &&
      release.deployment.environment === "production" &&
      Boolean(release.deployment.receiptRef);
    const readbackReady =
      release.readback?.status === "pass" &&
      release.readback.deploymentArtifactMatch === true &&
      release.readback.liveServerMatch === true &&
      Boolean(release.readback.readbackRef);
    return profileReady &&
      evidenceReady &&
      authorizationReady &&
      deploymentReady &&
      readbackReady
      ? "production_ready"
      : "live_readback_pending";
  }

  if (
    matrix?.candidateGate.status === "blocked" &&
    release.serverDerivedState === "candidate_ready"
  ) {
    return "candidate_blocked";
  }
  return release.serverDerivedState;
}

export function isPublicationProductionReady(
  release: PublicationReleaseProjection | null | undefined,
): boolean {
  return release?.derivedState === "production_ready";
}

export function publicationVerifiedDeploymentUrl(
  release: PublicationReleaseProjection | null | undefined,
): string | undefined {
  return hasVerifiedDeploymentReadback(release)
    ? release.deployment.url
    : undefined;
}

export function hasVerifiedDeploymentReadback(
  release: PublicationReleaseProjection | null | undefined,
): release is PublicationReleaseProjection & {
  deployment: PublicationDeploymentProjection & {
    status: "verified" | "rolled-back";
    receiptRef: PublicationContentRef;
  };
  readback: PublicationReadbackProjection & {
    status: "pass" | "rolled-back";
    readbackRef: PublicationContentRef;
  };
} {
  if (!release?.deployment?.receiptRef || !release.readback?.readbackRef)
    return false;
  const statusPairVerified =
    (release.deployment.status === "verified" &&
      release.readback.status === "pass") ||
    (release.deployment.status === "rolled-back" &&
      release.readback.status === "rolled-back");
  return (
    statusPairVerified &&
    release.readback.deploymentArtifactMatch === true &&
    release.readback.liveServerMatch === true
  );
}

function deriveClientBlockers(release: PublicationReleaseProjection): string[] {
  const blockers: string[] = [];
  const productionMatrix = effectiveProductionMatrix(release);
  blockers.push(...selectedMatrixBindingBlockers(release));
  if (release.sourceRevision.stale)
    blockers.push(
      "The saved project revision changed. Prepare a new candidate.",
    );
  if (release.targetProfile.lifecycle !== "active")
    blockers.push(
      `Target profile lifecycle is ${release.targetProfile.lifecycle}.`,
    );
  if (release.targetProfile.supportStatus !== "supported")
    blockers.push(
      `Target profile support is ${release.targetProfile.supportStatus}.`,
    );
  for (const gap of release.targetProfile.openGaps)
    blockers.push(`Target profile gap: ${gap}`);
  for (const axis of productionMatrix?.productionGate.missingAxes ?? [])
    blockers.push(`Missing ${axis} evidence.`);
  for (const axis of productionMatrix?.productionGate.failedAxes ?? [])
    blockers.push(`${axis} verification failed.`);
  if (
    release.serverDerivedState === "production_ready" &&
    release.derivedState !== "production_ready"
  ) {
    blockers.push(
      "Server readiness was capped because the signed authorization or the independent live check is incomplete.",
    );
  }
  if (
    release.serverDerivedState === "taken_down" &&
    release.derivedState !== "taken_down"
  ) {
    blockers.push(
      "Server take-down was capped until the exact removal receipt and an independent check that it is gone are verified.",
    );
  }
  return blockers;
}

function filterAllowedActions(
  release: PublicationReleaseProjection,
): PublicationReleaseAction[] {
  return uniqueStrings(release.allowedActions).filter(
    (action): action is PublicationReleaseAction => {
      if (!RELEASE_ACTIONS.has(action as PublicationReleaseAction))
        return false;
      if (
        release.sourceRevision.stale &&
        action !== "prepare-candidate" &&
        action !== "refresh-readback" &&
        action !== "rollback" &&
        action !== "take-down"
      )
        return false;
      if (action === "deploy-production") {
        return (
          release.authorization?.status === "verified" &&
          Boolean(release.promotionRef) &&
          effectiveProductionMatrix(release)?.productionGate.status ===
            "ready" &&
          release.targetProfile.lifecycle === "active" &&
          release.targetProfile.supportStatus === "supported" &&
          release.targetProfile.openGaps.length === 0
        );
      }
      if (action === "deploy-staging")
        return release.executionGrant?.status === "verified";
      if (action === "rollback") {
        if (
          release.deployment?.environment !== "preview" &&
          release.deployment?.environment !== "staging" &&
          release.deployment?.environment !== "production"
        )
          return false;
        return release.rollbackTargets.some(
          (target) => target.environment === release.deployment?.environment,
        );
      }
      if (action === "take-down") {
        return (
          hasVerifiedDeploymentReadback(release) &&
          (release.deployment.environment === "preview" ||
            release.deployment.environment === "staging" ||
            release.deployment.environment === "production") &&
          (release.deployment.operation === "deploy" ||
            release.deployment.operation === "rollback") &&
          Boolean(release.deployment.outputHash) &&
          Boolean(release.deployment.receiptRef)
        );
      }
      return true;
    },
  );
}

function effectiveProductionMatrix(
  release:
    | Omit<PublicationReleaseProjection, "derivedState">
    | PublicationReleaseProjection,
): PublicationVerificationMatrixProjection | undefined {
  const matrix =
    release.postDeployVerificationMatrix ?? release.verificationMatrix;
  return matrix &&
    verificationMatrixBindingBlockers(release, matrix).length === 0
    ? matrix
    : undefined;
}

function selectedMatrixBindingBlockers(
  release:
    | Omit<PublicationReleaseProjection, "derivedState">
    | PublicationReleaseProjection,
): string[] {
  const matrix =
    release.postDeployVerificationMatrix ?? release.verificationMatrix;
  return matrix ? verificationMatrixBindingBlockers(release, matrix) : [];
}

function verificationMatrixBindingBlockers(
  release:
    | Omit<PublicationReleaseProjection, "derivedState">
    | PublicationReleaseProjection,
  matrix: PublicationVerificationMatrixProjection,
): string[] {
  const blockers: string[] = [];
  if (matrix.targetProfileHash !== release.targetProfile.contentHash) {
    blockers.push(
      "Verification matrix target profile does not match this release.",
    );
  }
  if (!release.planRef || matrix.planHash !== release.planRef.contentHash) {
    blockers.push("Verification matrix plan does not match this release.");
  }
  if (
    !release.outputManifestRef ||
    matrix.outputManifestHash !== release.outputManifestRef.contentHash ||
    matrix.outputManifestHash !== release.outputHash
  ) {
    blockers.push(
      "Verification matrix output manifest does not match this release.",
    );
  }
  const proofArtifactKeys = new Set(
    release.proofArtifactRefs.map(
      (artifact) => `${artifact.axis}\u0000${artifact.ref}`,
    ),
  );
  for (const axis of matrix.axes) {
    if (axis.status !== "pass") continue;
    for (const evidenceRef of axis.evidenceRefs) {
      if (!proofArtifactKeys.has(`${axis.axis}\u0000${evidenceRef}`)) {
        blockers.push(
          `Verification evidence ${evidenceRef} for ${axis.axis} is missing from this release.`,
        );
      }
    }
  }
  return blockers;
}

function parseVerificationMatrix(
  value: unknown,
): PublicationVerificationMatrixProjection | undefined {
  if (!value) return undefined;
  const raw = record(value);
  if (raw.schemaVersion !== "cogo.publication-verification-matrix.v1")
    return undefined;
  const axesByName = new Map<
    PublicationVerificationAxis,
    PublicationVerificationAxisProjection
  >();
  for (const item of array(raw.axes)) {
    const axisRecord = record(item);
    const axis = text(axisRecord.axis);
    if (!axis || !AXES.has(axis)) continue;
    const status = normalizeOneOf(
      axisRecord.status,
      ["pass", "fail", "not-proven"],
      "not-proven",
    );
    axesByName.set(axis as PublicationVerificationAxis, {
      axis: axis as PublicationVerificationAxis,
      status,
      evidenceRefs: status === "pass" ? strings(axisRecord.evidenceRefs) : [],
      notes: strings(axisRecord.notes),
    });
  }
  const axes = PUBLICATION_VERIFICATION_AXES.map(
    (axis) =>
      axesByName.get(axis) ?? {
        axis,
        status: "not-proven" as const,
        evidenceRefs: [],
        notes: ["axis-result-missing-from-server-projection"],
      },
  );
  return {
    schemaVersion: "cogo.publication-verification-matrix.v1",
    targetId: requiredText(raw.targetId, "verificationMatrix.targetId"),
    targetProfileHash: requiredHash(
      raw.targetProfileHash,
      "verificationMatrix.targetProfileHash",
    ),
    planHash: requiredHash(raw.planHash, "verificationMatrix.planHash"),
    outputManifestHash: requiredHash(
      raw.outputManifestHash,
      "verificationMatrix.outputManifestHash",
    ),
    axes,
    candidateGate: deriveGate(
      axes,
      PUBLICATION_CANDIDATE_REQUIRED_VERIFICATION_AXES,
    ),
    productionGate: deriveGate(axes, PUBLICATION_VERIFICATION_AXES),
    productionPublishAllowed: false,
    contentHash: requiredHash(
      raw.contentHash,
      "verificationMatrix.contentHash",
    ),
  };
}

function deriveGate(
  axes: PublicationVerificationAxisProjection[],
  required: readonly PublicationVerificationAxis[],
): PublicationVerificationGateProjection {
  const byAxis = new Map(axes.map((axis) => [axis.axis, axis]));
  const missingAxes = required.filter(
    (axis) =>
      byAxis.get(axis)?.status !== "pass" &&
      byAxis.get(axis)?.status !== "fail",
  );
  const failedAxes = required.filter(
    (axis) => byAxis.get(axis)?.status === "fail",
  );
  return {
    status:
      missingAxes.length === 0 && failedAxes.length === 0 ? "ready" : "blocked",
    missingAxes,
    failedAxes,
  };
}

function parseAuthorization(
  value: unknown,
): PublicationAuthorizationProjection | undefined {
  if (!value) return undefined;
  const raw = record(value);
  return {
    status: normalizeOneOf(
      raw.status,
      ["pending", "verified", "rejected", "expired", "consumed"],
      "pending",
    ),
    authorizationRef: parseContentRef(raw.authorizationRef),
    ...(raw.environment === "production"
      ? { environment: "production" as const }
      : {}),
    ...(text(raw.expiresAt) ? { expiresAt: text(raw.expiresAt) } : {}),
  };
}

function parseExecutionGrant(
  value: unknown,
): PublicationExecutionGrantProjection | undefined {
  if (!value) return undefined;
  const raw = record(value);
  const environment =
    raw.environment === "test" ||
    raw.environment === "preview" ||
    raw.environment === "staging"
      ? raw.environment
      : undefined;
  return {
    status: normalizeOneOf(
      raw.status,
      ["pending", "verified", "rejected", "expired", "consumed"],
      "pending",
    ),
    grantRef: parseContentRef(raw.grantRef),
    ...(environment ? { environment } : {}),
    ...(text(raw.expiresAt) ? { expiresAt: text(raw.expiresAt) } : {}),
  };
}

function parseDeployment(
  value: unknown,
): PublicationDeploymentProjection | undefined {
  if (!value) return undefined;
  const raw = record(value);
  return {
    status: normalizeOneOf(
      raw.status,
      [
        "pending",
        "deploying",
        "reported",
        "readback-pending",
        "verified",
        "failed",
        "rolled-back",
        "taken-down",
      ],
      "pending",
    ),
    environment: normalizeOneOf(
      raw.environment,
      ["test", "preview", "staging", "production"],
      "preview",
    ),
    ...(raw.operation === "deploy" ||
    raw.operation === "rollback" ||
    raw.operation === "take-down"
      ? { operation: raw.operation }
      : {}),
    ...(text(raw.outputHash)
      ? { outputHash: requiredHash(raw.outputHash, "deployment.outputHash") }
      : {}),
    ...(text(raw.provider) ? { provider: text(raw.provider) } : {}),
    ...(text(raw.deploymentId) ? { deploymentId: text(raw.deploymentId) } : {}),
    ...(text(raw.url) ? { url: text(raw.url) } : {}),
    receiptRef: parseContentRef(raw.receiptRef),
    failureReasons: strings(raw.failureReasons),
  };
}

function parseReadback(
  value: unknown,
): PublicationReadbackProjection | undefined {
  if (!value) return undefined;
  const raw = record(value);
  return {
    status: normalizeOneOf(
      raw.status,
      ["pending", "pass", "fail", "rolled-back", "taken-down"],
      "pending",
    ),
    readbackRef: parseContentRef(raw.readbackRef),
    ...(typeof raw.deploymentArtifactMatch === "boolean"
      ? { deploymentArtifactMatch: raw.deploymentArtifactMatch }
      : {}),
    ...(typeof raw.liveServerMatch === "boolean"
      ? { liveServerMatch: raw.liveServerMatch }
      : {}),
    ...(text(raw.checkedAt) ? { checkedAt: text(raw.checkedAt) } : {}),
    failureReasons: strings(raw.failureReasons),
  };
}

function parseRollbackTargets(
  value: unknown,
): PublicationRollbackTargetProjection[] {
  const targets: PublicationRollbackTargetProjection[] = [];
  const seen = new Set<string>();
  for (const item of array(value)) {
    const raw = record(item);
    const releaseId = text(raw.releaseId);
    const outputHash = text(raw.outputHash);
    const receiptRef = parseContentRef(raw.receiptRef);
    const readbackRef = parseContentRef(raw.readbackRef);
    const environment =
      raw.environment === "test" ||
      raw.environment === "preview" ||
      raw.environment === "staging" ||
      raw.environment === "production"
        ? raw.environment
        : undefined;
    if (
      !releaseId ||
      !isHash(outputHash) ||
      !receiptRef ||
      !readbackRef ||
      !environment
    )
      continue;
    const key = `${receiptRef.ref}\u0000${receiptRef.contentHash}`;
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push({
      releaseId,
      receiptRef,
      outputHash,
      environment,
      readbackRef,
    });
  }
  return targets;
}

function parseContentRef(value: unknown): PublicationContentRef | undefined {
  if (!value) return undefined;
  const raw = record(value);
  const ref = text(raw.ref);
  const contentHash = text(raw.contentHash);
  return ref && isHash(contentHash) ? { ref, contentHash } : undefined;
}

function parseProofArtifactRefs(value: unknown): PublicationProofArtifactRef[] {
  const byRef = new Map<string, PublicationProofArtifactRef>();
  for (const item of array(value)) {
    const raw = record(item);
    const ref = text(raw.ref);
    const contentHash = text(raw.contentHash);
    const axis = text(raw.axis);
    if (!ref || !isHash(contentHash) || !axis || !AXES.has(axis)) continue;
    byRef.set(`${axis}\u0000${ref}`, {
      ref,
      contentHash,
      axis: axis as PublicationVerificationAxis,
    });
  }
  return [...byRef.values()].sort(
    (left, right) =>
      left.ref.localeCompare(right.ref) || left.axis.localeCompare(right.axis),
  );
}

function normalizeReleaseState(value: unknown): PublicationReleaseState {
  const state = text(value) as PublicationReleaseState | undefined;
  return state && RELEASE_STATES.has(state) ? state : "draft";
}

function normalizeOneOf<const T extends readonly string[]>(
  value: unknown,
  allowed: T,
  fallback: T[number],
): T[number] {
  const normalized = text(value);
  return normalized && (allowed as readonly string[]).includes(normalized)
    ? (normalized as T[number])
    : fallback;
}

function requiredText(value: unknown, label: string): string {
  const normalized = text(value);
  if (!normalized) throw new Error(`Publication release is missing ${label}`);
  return normalized;
}

function requiredHash(value: unknown, label: string): string {
  const normalized = requiredText(value, label);
  if (!isHash(normalized))
    throw new Error(`Publication release has invalid ${label}`);
  return normalized;
}

function isHash(value: string | undefined): value is string {
  return Boolean(value && /^sha256:[a-f0-9]{64}$/.test(value));
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function strings(value: unknown): string[] {
  return array(value)
    .map(text)
    .filter((item): item is string => Boolean(item));
}

function uniqueStrings<T extends string>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
